import { useEffect } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { VideoFrame } from '../ui/bits.tsx';
import { rich } from '../ui/Rich.tsx';
import { asset } from '../engine/assets.ts';
import { CREAM, CORAL, INK, LINE, MUTED, ORANGE, SAND, SHADOW_CARD } from '../ui/theme.ts';

const SEGMENT_FILL: Record<string, string> = {
  removed: CORAL,
  coldOpen: `linear-gradient(90deg,${CORAL},${ORANGE})`,
  kept: CREAM,
  trimmed: SAND,
};

/**
 * interstitial · variant video (asset + optional timeline bar from shared.timeline)
 * · variant photo (a still from `asset`, then the copy).
 */
export function Interstitial({ step, content, ctx }: StepProps) {
  const video = step.variant === 'video';
  const photo = step.variant === 'photo';
  const timeline = step.timeline ? ctx.shared.timeline : undefined;
  const slot = step.asset ?? content.asset;

  useEffect(() => {
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.continue ?? 'Continue', caption: content.ctaCaption, icon: content.ctaIcon, onPress: () => ctx.next() });
  }, [step.id, content.cta]);

  return (
    <>
      <TitleBlock
        label={content.label}
        title={content.title}
        highlight={content.highlight}
        subtitle={content.subtitle}
        icon={video || photo ? undefined : content.icon}
      />

      {photo && slot ? (
        <div style={{ flex: 'none', width: '100%', aspectRatio: '13 / 8', borderRadius: 20, overflow: 'hidden', background: CREAM, boxShadow: SHADOW_CARD }}>
          <img src={asset(slot)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
      ) : null}

      {video ? (
        <>
          <VideoFrame slot={step.asset ?? 'interstitial_gallery'} muteLabel={ctx.shared.ui?.mutedLoop ?? 'muted · loop'} />
          {timeline ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', gap: 3 }}>
                {timeline.segments.map((s, i) => (
                  <span
                    key={s.label}
                    style={{ width: `${s.pct}%`, background: SEGMENT_FILL[s.kind] ?? LINE, animation: `fk-fill ${0.6 + i * 0.3}s ease-out` }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: MUTED }}>
                {timeline.segments.map((s) => (
                  <span key={s.label}>{s.label}</span>
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {content.stat?.value ? (
        <div
          style={{
            padding: 'clamp(12px, 3.6vw, 18px) clamp(14px, 4vw, 20px)',
            borderRadius: 20,
            background: CREAM,
            border: `2px solid ${LINE}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 'var(--fk-stat)', fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1 }}>{content.stat.value}</div>
          <div className="fk-copy" style={{ fontSize: 'clamp(12px, 3.4vw, 13px)', fontWeight: 600, color: MUTED, maxWidth: '30ch', margin: '0 auto' }}>{content.stat.caption}</div>
        </div>
      ) : null}

      {content.paragraphs?.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'center', color: MUTED, fontSize: 'var(--fk-sub)', fontWeight: 600 }}>
          {content.paragraphs.map((p, i) => (
            <p key={i} className="fk-copy" style={{ margin: '0 auto', maxWidth: '36ch' }}>
              {rich(p, INK)}
            </p>
          ))}
        </div>
      ) : null}
    </>
  );
}
